import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, MessageCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { parseUTCTimestamp } from '../../utils/dateUtils';

interface IntegrationsTabProps {
  isOpen: boolean;
}

export const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ isOpen }) => {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [telegramLinkUrl, setTelegramLinkUrl] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  // Check for Google Calendar OAuth callback
  useEffect(() => {
    if (!isOpen) return;

    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const scope = urlParams.get('scope');

    if (authCode && user && !window.location.pathname.includes('/auth')) {
      if (scope && scope.includes('calendar')) {
        // Clear URL params and path after processing
        window.history.replaceState({}, document.title, '/');

        if (!user.google_calendar_enabled) {
          const processedCode = sessionStorage.getItem('gcal_processed_code');

          if (processedCode !== authCode) {
            sessionStorage.setItem('gcal_processed_code', authCode);

            // Exchange the authorization code for tokens
            (async () => {
              try {
                const redirectUri = `${window.location.origin}/settings`;
                const response = await api.post('/events/google-calendar/authorize', {
                  authorization_code: authCode,
                  redirect_uri: redirectUri
                });
                if (response.data.success) {
                  // Auto-sync immediately after connecting
                  try {
                    await api.post('/events/google-calendar/sync');
                  } catch {
                    // Sync failure is non-fatal; user can retry manually
                  }
                  await refreshUser();
                  await queryClient.invalidateQueries({ queryKey: ['events'] });
                } else {
                  alert(`Failed to connect Google Calendar: ${response.data.message}`);
                }
              } catch (error: unknown) {
                const err = error instanceof Error ? error : null;
                const axiosErr = error as { response?: { status?: number; data?: { detail?: string } }; message?: string };
                if (axiosErr.response?.status === 401) {
                  alert('Authentication error: Please log in again and retry');
                } else {
                  alert(`Error connecting Google Calendar: ${axiosErr.response?.data?.detail || err?.message || 'Unknown error'}`);
                }
              }
            })();
          }
        }
      }
    }
  }, [isOpen, user, refreshUser]);

  // Reset telegram link URL when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTelegramLinkUrl(null);
    }
  }, [isOpen]);

  const handleGoogleCalendarConnect = () => {
    // Clear any previous auth codes
    sessionStorage.removeItem('gcal_processed_code');

    // Initiate Google Calendar OAuth flow
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/settings`;
    const scope = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events';

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(scope)}&` +
      `access_type=offline&` +
      `prompt=consent`;

    window.location.href = authUrl;
  };

  const handleGoogleCalendarSync = async () => {
    setIsSyncing(true);
    try {
      const response = await api.post('/events/google-calendar/sync');
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      alert(`Successfully synced ${response.data.synced_count} events from Google Calendar`);
    } catch (error: unknown) {
      const axiosErr = error as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      if (axiosErr.response?.status === 401) {
        alert('Authentication error. Please reconnect Google Calendar.');
      } else {
        alert(`Error syncing Google Calendar: ${axiosErr.response?.data?.detail || axiosErr.message || 'Unknown error'}`);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar? This will remove all synced events.')) {
      return;
    }

    try {
      await api.delete('/events/google-calendar/disconnect');

      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      alert('Google Calendar disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      alert('Error disconnecting Google Calendar');
    }
  };

  const handleTelegramConnect = async () => {
    setIsGeneratingLink(true);
    try {
      const response = await api.post('/auth/telegram/generate-link');
      setTelegramLinkUrl(response.data.link_url);
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { detail?: string } }; message?: string };
      alert(`Error generating Telegram link: ${axiosErr.response?.data?.detail || axiosErr.message || 'Unknown error'}`);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleTelegramDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Telegram?')) {
      return;
    }

    try {
      await api.delete('/auth/telegram/disconnect');
      await refreshUser();
      setTelegramLinkUrl(null);
    } catch (error) {
      console.error('Error disconnecting Telegram:', error);
      alert('Error disconnecting Telegram');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Integrations</h3>

      {/* Google Calendar */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h4 className="font-medium text-gray-900 dark:text-gray-100">Google Calendar</h4>
        </div>

        {user?.google_calendar_enabled ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-green-600 dark:text-green-400">Connected</span>
              {user.google_calendar_last_sync && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Last synced: {parseUTCTimestamp(user.google_calendar_last_sync).toLocaleString()}
                </span>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleGoogleCalendarSync}
                disabled={isSyncing}
                className={`px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${isSyncing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSyncing ? 'Syncing...' : 'Sync Now'}
              </button>
              <button
                onClick={handleGoogleCalendarDisconnect}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Disconnect
              </button>
            </div>

            <p className="text-xs text-gray-600 dark:text-gray-400">
              Your Google Calendar events are synced and appear in your calendar views.
            </p>
          </div>
        ) : !user?.google_calendar_enabled && user?.google_calendar_last_sync ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-md">
              <span className="text-sm text-amber-800 dark:text-amber-200">
                Authorization expired. Reconnect to resume calendar sync.
              </span>
            </div>
            <button
              onClick={handleGoogleCalendarConnect}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
            >
              Reconnect Google Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Connect your Google Calendar to view your events alongside your tasks.
            </p>
            <button
              onClick={handleGoogleCalendarConnect}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Connect Google Calendar
            </button>
          </div>
        )}
      </div>

      {/* Telegram */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-3">
          <MessageCircle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h4 className="font-medium text-gray-900 dark:text-gray-100">Telegram</h4>
        </div>

        {user?.telegram_chat_id ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-green-600 dark:text-green-400">Connected</span>
              {user.telegram_linked_time && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Linked: {parseUTCTimestamp(user.telegram_linked_time).toLocaleString()}
                </span>
              )}
            </div>

            <button
              onClick={handleTelegramDisconnect}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Disconnect
            </button>

            <p className="text-xs text-gray-600 dark:text-gray-400">
              Send messages to your Telegram bot to interact with your assistant on the go.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Connect Telegram to chat with your assistant directly from the Telegram app.
            </p>

            {telegramLinkUrl ? (
              <div className="space-y-2">
                <a
                  href={telegramLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Open in Telegram
                  <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Click the button above, then tap <b>Start</b> in Telegram to complete linking.
                </p>
              </div>
            ) : (
              <button
                onClick={handleTelegramConnect}
                disabled={isGeneratingLink}
                className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${isGeneratingLink ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isGeneratingLink ? 'Generating link...' : 'Connect Telegram'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
